import React from 'react';
import { styled } from 'baseui';
import { LabelMedium, LabelSmall, HeadingMedium } from 'baseui/typography';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const CardsContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
  gap: '16px',
  marginBottom: '24px',
  '@media (max-width: 768px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  // Two columns on a phone as well (six cards in one column were three screens tall).
  '@media (max-width: 480px)': {
    gap: '10px',
  },
});

const KPICard = styled('div', {
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  '@media (max-width: 480px)': {
    padding: '14px',
  },
});

const KPIValue = styled('div', {
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
});

const TrendIndicatorBase = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '14px',
  fontWeight: 500,
});

interface KPIData {
  label: string;
  value: number | string;
  change?: number;
  format?: 'number' | 'percentage' | 'decimal';
}

interface KPICardsProps {
  kpis: KPIData[];
}

export const KPICards: React.FC<KPICardsProps> = ({ kpis }) => {
  const formatValue = (value: number | string, format?: 'number' | 'percentage' | 'decimal') => {
    if (typeof value === 'string') return value;
    
    switch (format) {
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'decimal':
        return value.toFixed(2);
      default:
        return value.toLocaleString();
    }
  };

  const getTrendIcon = (change: number) => {
    if (change > 0) return <TrendingUp size={16} />;
    if (change < 0) return <TrendingDown size={16} />;
    return <Minus size={16} />;
  };

  const getTrendColor = (change: number): string => {
    if (change > 0) return '#10b981';
    if (change < 0) return '#ef4444';
    return '#6b7280';
  };

  return (
    <CardsContainer>
      {kpis.map((kpi, index) => (
        <KPICard key={index}>
          <LabelSmall color="#6b7280" margin={0}>{kpi.label}</LabelSmall>
          <KPIValue>
            <HeadingMedium margin={0}>
              {formatValue(kpi.value, kpi.format)}
            </HeadingMedium>
          </KPIValue>
          {kpi.change !== undefined && (
            <TrendIndicatorBase style={{ color: getTrendColor(kpi.change) }}>
              {getTrendIcon(kpi.change)}
              <span>{Math.abs(kpi.change).toFixed(1)}%</span>
            </TrendIndicatorBase>
          )}
        </KPICard>
      ))}
    </CardsContainer>
  );
};
